import React, { useEffect, useState } from 'react';
import useStore from '../../store/useStore';
import PeriodSelect from '../common/PeriodSelect';
import EditorDrawer from './EditorDrawer';
import { widgetLabel, widgetKindLabel } from './editorUtils';

const SPARKLINE_WINDOWS = [
  { value: 60, label: 'Last hour' },
  { value: 360, label: 'Last 6 hours' },
  { value: 1440, label: 'Last 24 hours' },
  { value: 10080, label: 'Last 7 days' },
];

/**
 * Settings for one widget on this dashboard. Changes here only affect this
 * widget; shared things (a saved report, a live metric's definition) open
 * their own editors and say so.
 */
const WidgetInspector = ({ widget, onSave, onClose, onRemove, onDuplicate, onEditReport, onEditDefinition, saving = false }) => {
  const config = widget.config || {};
  const kind = widget.widget_kind || 'metric';
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [form, setForm] = useState(() => initialForm(widget));
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => { setForm(initialForm(widget)); setConfirmRemove(false); }, [widget.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const defaultTitle = widgetLabel({ ...widget, config: { ...config, title: undefined } });

  const submit = (e) => {
    e.preventDefault();
    onSave(buildConfig(widget, form));
  };

  return (
    <EditorDrawer
      title="Widget settings"
      subtitle={`${widgetKindLabel(widget)} · ${defaultTitle}`}
      onClose={onClose}
      labelId="widget-inspector-title"
      footer={(
        <div className="inspector-footer">
          {confirmRemove ? (
            <>
              <span className="inspector-confirm">Remove this widget?</span>
              <button type="button" className="btn-danger-small" onClick={onRemove}>Remove</button>
              <button type="button" className="btn-secondary-small" onClick={() => setConfirmRemove(false)}>Keep</button>
            </>
          ) : (
            <>
              <button type="button" className="btn-secondary-small" onClick={() => setConfirmRemove(true)}>Remove</button>
              {onDuplicate && <button type="button" className="btn-secondary-small" onClick={onDuplicate}>Duplicate</button>}
              <span className="inspector-spacer" />
              <button type="submit" form="widget-inspector-form" className="btn-primary-small" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </>
          )}
        </div>
      )}
    >
      <form id="widget-inspector-form" className="inspector-form" onSubmit={submit}>
        <label className="inspector-field">
          <span>Title</span>
          <input value={form.title} placeholder={defaultTitle} onChange={(e) => update({ title: e.target.value })} />
        </label>
        <small className="inspector-hint">Leave blank to use “{defaultTitle}”.</small>

        {kind === 'metric' && (
          <>
            {widget.widget_type === 'count' && (
              <>
                <label className="inspector-field">
                  <span>Unit</span>
                  <input value={form.unit} placeholder="e.g. orders, £, %" onChange={(e) => update({ unit: e.target.value })} />
                </label>
                <label className="inspector-check">
                  <input type="checkbox" checked={form.invertTrendColors} onChange={(e) => update({ invertTrendColors: e.target.checked })} />
                  <span>Show increases in red (for backlogs and error counts)</span>
                </label>
              </>
            )}
            {widget.widget_type === 'gauge' && (
              <div className="inspector-row">
                <label className="inspector-field"><span>Minimum</span><input type="number" value={form.min} onChange={(e) => update({ min: e.target.value })} /></label>
                <label className="inspector-field"><span>Maximum</span><input type="number" value={form.max} onChange={(e) => update({ max: e.target.value })} /></label>
              </div>
            )}
            {(widget.widget_type === 'count' || widget.widget_type === 'gauge') && (
              <>
                <label className="inspector-check">
                  <input type="checkbox" checked={form.sparkline} onChange={(e) => update({ sparkline: e.target.checked })} />
                  <span>Show a trend line of recent values</span>
                </label>
                {form.sparkline && (
                  <label className="inspector-field">
                    <span>Trend covers</span>
                    <select value={form.sparklineMinutes} onChange={(e) => update({ sparklineMinutes: Number(e.target.value) })}>
                      {SPARKLINE_WINDOWS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                )}
              </>
            )}
            <div className="inspector-shared">
              <p>Thresholds, colours and the widget type belong to the <strong>{widget.display_name || widget.metric_key}</strong> metric and apply on every dashboard.</p>
              {isAdmin && onEditDefinition && <button type="button" className="btn-secondary-small" onClick={onEditDefinition}>Edit metric definition…</button>}
            </div>
          </>
        )}

        {kind === 'dataset' && (
          <>
            {widget.report && (widget.report.cubeQuery?.timeDimensions || []).length > 0 && (
              <>
                <div className="inspector-field">
                  <span>Opens on</span>
                  <PeriodSelect ariaLabel="Opens on" value={form.period} onChange={(v) => update({ period: v })} ranges="calendar-financial"
                    emptyLabel={form.followPeriod ? "The dashboard's period" : "The report's own period"} />
                </div>
                <small className="inspector-hint">Viewers can change the period under the widget; their choice isn't saved.</small>
              </>
            )}
            <label className="inspector-check">
              <input type="checkbox" checked={form.followPeriod} onChange={(e) => update({ followPeriod: e.target.checked })} />
              <span>Use the dashboard's period when none is set here</span>
            </label>
            <div className="inspector-shared">
              <p>{widget.report
                ? <>The data, chart type and filters come from the saved report <strong>{widget.report.name}</strong>. Changing it updates every dashboard that shows it.</>
                : <>This widget uses an older report format. Editing it saves it as a reusable report.</>}
              </p>
              {onEditReport && <button type="button" className="btn-secondary-small" onClick={onEditReport}>{widget.report ? 'Open report to edit' : 'Edit report…'}</button>}
            </div>
          </>
        )}

        {kind === 'statement' && (
          <>
            <label className="inspector-check">
              <input type="checkbox" checked={form.followPeriod} onChange={(e) => update({ followPeriod: e.target.checked })} />
              <span>Follow the dashboard period</span>
            </label>
            <div className="inspector-field">
              <span>{form.followPeriod ? 'Period when the dashboard has none set' : 'Period'}</span>
              <PeriodSelect ariaLabel={form.followPeriod ? 'Period when the dashboard has none set' : 'Period'} value={form.period}
                onChange={(v) => update({ period: v })} emptyLabel="Statement default" ranges="financial-calendar" />
            </div>
            <label className="inspector-field">
              <span>Budget version</span>
              <input value={form.budgetVersion} placeholder="Latest" onChange={(e) => update({ budgetVersion: e.target.value })} />
            </label>
            <small className="inspector-hint">Leave blank to use the latest version.</small>
          </>
        )}

      </form>
    </EditorDrawer>
  );
};

function initialForm(widget) {
  const c = widget.config || {};
  const kind = widget.widget_kind || 'metric';
  return {
    title: c.title || '',
    unit: c.unit || '',
    invertTrendColors: Boolean(c.invertTrendColors),
    min: c.min ?? widget.metric_config?.min ?? '',
    max: c.max ?? widget.metric_config?.max ?? '',
    sparkline: c.sparkline !== false,
    sparklineMinutes: Number(c.sparklineMinutes) || 1440,
    followPeriod: kind === 'statement' ? c.inheritDateRange !== false : (c.displayOverrides?.inheritDateRange ?? widget.report?.display?.inheritDateRange) !== false,
    period: c.period || '',
    budgetVersion: c.budgetVersion || '',
  };
}

/** Merge the form into the widget's config, dropping empty values. */
export function buildConfig(widget, form) {
  const kind = widget.widget_kind || 'metric';
  const next = { ...(widget.config || {}) };
  const set = (key, value) => { if (value === '' || value === null || value === undefined) delete next[key]; else next[key] = value; };
  set('title', form.title.trim());
  if (kind === 'metric') {
    if (widget.widget_type === 'count') {
      set('unit', form.unit.trim());
      set('invertTrendColors', form.invertTrendColors || undefined);
    }
    if (widget.widget_type === 'gauge') {
      set('min', form.min === '' ? '' : Number(form.min));
      set('max', form.max === '' ? '' : Number(form.max));
    }
    set('sparkline', form.sparkline ? undefined : false);
    set('sparklineMinutes', form.sparkline && form.sparklineMinutes !== 1440 ? form.sparklineMinutes : undefined);
  }
  if (kind === 'dataset') {
    const overrides = { ...(next.displayOverrides || {}), inheritDateRange: form.followPeriod };
    next.displayOverrides = overrides;
    set('period', form.period);
  }
  if (kind === 'statement') {
    next.inheritDateRange = form.followPeriod;
    set('period', form.period);
    set('budgetVersion', form.budgetVersion.trim());
  }
  return next;
}

export default WidgetInspector;
